import { api } from '@/global/services/api'
import { isObject } from 'lodash'
import { dateService, FORMATS } from '@/global/services/date'
import documentsHistoryHelper from '../documents-history-helper'
import { notify } from '@/global/services/notification/notificationBus'
import tabsHelper from '../tabs-helper'

const DOCUMENTS_HISTORY_DATA_KEY = 'approvalDocuments'
const DOCUMENT_TYPE = 'consent'
export default {

  mixins: [
    documentsHistoryHelper,
    tabsHelper
  ],

  data () {
    return {
      approvalHistoryVisible: false,
      approvalShowEditButtons: false,
      approvalEditState: false,
      approvalShowDocumentPreview: false,
      approvalDocument: null,
      approvalDataForGenerationVisible: true,
      approvalTitle: this.$t('road-maintenance/aggregator.approval'),
      approvalBaseTitle: this.$t('road-maintenance/aggregator.approval'),
      approvalValidationErrors: {},
      approvalUploadAttachmentEvent: null,
      approvalDocumentsVisible: true,
      approvalAttachmentsColumnsConfig: [],
      approvalDocumentHistoryLoaded: false,
      approvalRefreshDisabled: false,
      approvalDocumentLoaders: [],
      showApprovalTabLoader: null
    }
  },

  computed: {
    approvalConfig () {
      return {
        title: this.$t('road-maintenance/aggregator.approval'),
        visible: this.$route.query.category !== 'request_new',
        key: 'approval',
        validationErrors: this.approvalValidationErrors,
        apiConfig: {
          post: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}/store`,
            usePatch: false,
            method: 'post'
          },
          get: {
            module: 'road-maintenance',
            route: `traffic-regulation/${this.$route.params.id}/${DOCUMENT_TYPE}`,
            method: 'get'
          }
        },
        edit: this.approvalEditState,
        editUpdate: (event) => this.onApprovalEditUpdate(event),
        dataFetched: (event) => this.onApprovalDataFetched(event),
        afterCancel: (event) => this.onApprovalDataFetched(event),
        afterSave: (event) => this.onApprovalDataFetched(event, true),
        modifyDataBeforeSend: (event) => this.onApprovalModifyData(event),
        showLoader: this.showApprovalTabLoader,
        hiddenFormKeys: [
          'erp_reference_number',
          'additional_terms',
          'payment_type',
          'additional_terms_note',
          'html',
          'documents',
          'files'
        ],
        tabContent: [
          {
            component: this.tabActionsComponent,
            title: this.approvalTitle,
            showBackButton: {
              onGoBack: () => this.goBack()
            },
            showEditButtons: this.approvalShowEditButtons,
            forceAdditionalButtonsDisplay: true,
            additional_buttons: [
              {
                type: 'base',
                appendIcon: 'mdi-refresh',
                visible: !this.approvalShowEditButtons,
                disabled: this.approvalRefreshDisabled,
                click: async (event) => {
                  const { fetchTabData } = event || {}
                  this.approvalRefreshDisabled = true

                  await fetchTabData?.()
                  await this.fetchHistoryDocuments(DOCUMENT_TYPE, DOCUMENTS_HISTORY_DATA_KEY)

                  setTimeout(() => {
                    this.approvalRefreshDisabled = false
                  }, this.refreshTabBtnTimeout)
                }
              },
              {
                type: 'base',
                label: this.$t('fleet/documents/prl_cargo.change_status'),
                prependIcon: 'mdi-swap-horizontal',
                visible: !this.approvalShowEditButtons && !!this.permissions?.canChangeStatus,
                click: (event) => this.onChangeStatus(event)
              },
              {
                type: 'dropdown',
                buttonType: 'base',
                label: this.$t('road-maintenance/aggregator.generate_document'),
                color: '#65AD45',
                class: 'generate-document-btn',
                visible: !this.approvalShowEditButtons && !!this.permissions?.canCreate,
                menuContentClass: 'menu-class',
                component: this.documentsGenerationDropdownMenuComponent,
                hasHistoryData: this.logsData?.[DOCUMENTS_HISTORY_DATA_KEY]?.length,
                menuItemClick: (event) => this.approvalMenuItemClick(event)
              },
              {
                type: 'base',
                label: this.viewPdfBtnLabel,
                color: '#6e736d',
                visible: this.approvalShowEditButtons,
                class: 'generate-document-btn',
                loaderConfig: {
                  opacity: 0,
                  color: '#FFFFFFF'
                },
                showLoader: this.showPreviewLoader,
                click: (event) => this.previewDocument(event, DOCUMENT_TYPE, this.approvalBaseTitle)
              }
            ],
            onCancelTabSave: () => this.onApprovalCancelTab(),
            onSaveClick: (event) => this.onApprovalSave(event)
          },
          // Data for document generation
          {
            component: this.columnsContentComponent,
            title: this.$t('road-maintenance/aggregator.data_for_approval_generation'),
            visible: this.approvalDataForGenerationVisible,
            rows: [
              {
                type: 'fields',
                columnsCount: 3,
                fields: [
                  {
                    key: 'doc_class',
                    label: this.$t('road-maintenance/aggregator.class'),
                    row: 1,
                    column: 1,
                    colSpan: 1,
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'registry_number',
                    row: 1,
                    column: 2,
                    colSpan: 1,
                    label: this.$t('road-maintenance/excavation-service/tabs/approval/columns.registration_number'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'created_at',
                    type: 'datetime-picker',
                    subType: 'date',
                    row: 1,
                    column: 3,
                    colSpan: 1,
                    appendIcon: 'mdi-calendar',
                    format: FORMATS.date,
                    placeholder: FORMATS.date,
                    label: this.$t('road-maintenance/aggregator.document_date'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.date, true)) {
                        updateValidationErrors({
                          created_at: this.$t('base.invalid_date').replace(':format', FORMATS.date)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate,
                    doubleClickEdit: false
                  },
                  {
                    key: 'applicant',
                    row: 2,
                    column: 1,
                    colSpan: 1,
                    label: this.$t('road-maintenance/prp.request_applicant'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'addresses',
                    row: 2,
                    column: 2,
                    colSpan: 2,
                    label: this.$t('road-maintenance/aggregator.request_locations'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'work_type',
                    type: 'textarea',
                    row: 3,
                    column: 1,
                    colSpan: 1,
                    label: this.$t('road-maintenance/aggregator.type_of_works_or_actions'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'regulation_setter',
                    row: 3,
                    column: 2,
                    colSpan: 1,
                    label: this.$t('road-maintenance/codebook_type.regulation_setter'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_name',
                    row: 3,
                    column: 3,
                    colSpan: 1,
                    label: this.$t('road-maintenance/aggregator.contractor_name'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_responsible',
                    row: 4,
                    column: 1,
                    colSpan: 1,
                    label: this.$t('road-maintenance/aggregator.contractor_responsible_person'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_phone',
                    row: 4,
                    column: 2,
                    colSpan: 1,
                    label: this.$t('road-maintenance/aggregator.contractor_phone'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'contractor_email',
                    row: 4,
                    column: 3,
                    colSpan: 1,
                    label: this.$t('road-maintenance/aggregator.contractor_email'),
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'road_authority_responsible_person',
                    row: 5,
                    column: 1,
                    colSpan: 1.5,
                    label: this.$t('road-maintenance/dropdown-types.zc_responsible_person'),
                    creatable: true,
                    editable: true,
                    unlock: !!this.permissions?.canCreate,
                    visible: true
                  },
                  {
                    type: 'plain-text',
                    text: this.$t('road-maintenance/aggregator.consent_valid'),
                    row: 6,
                    column: 1,
                    colSpan: 1.5,
                    class: 'time-period-text',
                    colClass: 'time-period-column',
                    visible: true
                  },
                  {
                    type: 'plain-text',
                    text: this.$t('road-maintenance/aggregator.requested_date_from_request'),
                    row: 6,
                    column: 2,
                    colSpan: 1.5,
                    class: 'time-period-text',
                    colClass: 'time-period-column',
                    visible: true
                  },
                  {
                    key: 'valid_from',
                    type: 'datetime-picker',
                    row: 7,
                    column: 1,
                    colSpan: 0.75,
                    appendIcon: 'mdi-calendar',
                    format: FORMATS.datetimeShort,
                    placeholder: FORMATS.datetimeShort,
                    label: this.$t('road-maintenance/aggregator.date_from'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.datetimeShort, true)) {
                        updateValidationErrors({
                          valid_from: this.$t('base.invalid_date').replace(':format', FORMATS.datetimeShort)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate,
                    doubleClickEdit: false
                  },
                  {
                    key: 'valid_to',
                    type: 'datetime-picker',
                    row: 7,
                    column: 1,
                    colSpan: 0.75,
                    appendIcon: 'mdi-calendar',
                    format: FORMATS.datetimeShort,
                    placeholder: FORMATS.datetimeShort,
                    label: this.$t('road-maintenance/aggregator.date_to'),
                    onInput: (value, event) => {
                      const { updateValidationErrors } = event || {}

                      if (value && typeof updateValidationErrors === 'function' && !dateService.isValid(value, FORMATS.datetimeShort, true)) {
                        updateValidationErrors({
                          valid_to: this.$t('base.invalid_date').replace(':format', FORMATS.datetimeShort)
                        })
                      }
                    },
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate,
                    doubleClickEdit: false
                  },
                  {
                    key: 'date_time_from',
                    row: 7,
                    column: 2,
                    colSpan: 0.75,
                    appendIcon: 'mdi-calendar',
                    label: this.$t('road-maintenance/aggregator.date_from'),
                    onInput: (value, event) => {
                      const { updateData, formData } = event || {}
                      if (value && typeof updateData === 'function' && !formData.valid_from) {
                        updateData({
                          valid_from: value
                        })
                      }
                    },
                    creatable: false,
                    editable: false,
                    visible: true
                  },
                  {
                    key: 'date_time_to',
                    row: 7,
                    column: 2,
                    colSpan: 0.75,
                    appendIcon: 'mdi-calendar',
                    label: this.$t('road-maintenance/aggregator.date_to'),
                    onInput: (value, event) => {
                      const { updateData, formData } = event || {}
                      if (value && typeof updateData === 'function' && !formData.valid_to) {
                        updateData({
                          valid_to: value
                        })
                      }
                    },
                    creatable: false,
                    editable: false,
                    visible: true
                  }
                ]
              },
              {
                component: this.accountingFieldsRowComponent,
                disabled: !this.permissions?.canCreate
              },
              {
                type: 'fields',
                columnsCount: 3,
                fields: [
                  {
                    key: 'restoration_responsibility',
                    label: this.$t('road-maintenance/aggregator.restoring_costs'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      custom_display_value: (data) => {
                        return data?.description ? this.$t(data.description) : null
                      },
                      returnValue: 'object',
                      form_value: 'name'
                    },
                    items: this.dropdownValues?.traffic_regulation_restoration_responsibility || [],
                    row: 1,
                    column: 1,
                    colSpan: 1,
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate,
                    doubleClickEdit: false
                  },
                  {
                    key: 'damage_responsibility',
                    label: this.$t('road-maintenance/aggregator.all_damages_incurring'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      custom_display_value: (data) => {
                        return data?.description ? this.$t(data.description) : null
                      },
                      returnValue: 'object',
                      form_value: 'name'
                    },
                    items: this.dropdownValues?.traffic_regulation_restoration_responsibility || [],
                    row: 1,
                    column: 2,
                    colSpan: 1,
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate,
                    doubleClickEdit: false
                  },
                  {
                    key: 'additional_costs_responsibility',
                    label: this.$t('road-maintenance/aggregator.costs_obligated'),
                    type: 'autocomplete',
                    autocomplete_options: {
                      custom_display_value: (data) => {
                        return data?.description ? this.$t(data.description) : null
                      },
                      returnValue: 'object',
                      form_value: 'name'
                    },
                    items: this.dropdownValues?.traffic_regulation_restoration_responsibility || [],
                    row: 1,
                    column: 3,
                    colSpan: 1,
                    creatable: true,
                    editable: true,
                    visible: true,
                    unlock: !!this.permissions?.canCreate,
                    doubleClickEdit: false
                  }
                ]
              },
              {
                component: this.conditionFieldsRowComponent,
                disabled: !this.permissions?.canCreate
              },
              {
                component: this.rowBlocksDataComponent,
                mainClasses: 'aggregator-approval-blocks-main',
                titleClasses: 'aggregator-approval-internal-title',
                wrapperClasses: 'aggregator-approval-card-text',
                title: this.$t('road-maintenance/aggregator.attachments'),
                cardTitle: 'aggregator-approval-card-title',
                visible: this.approvalDocumentsVisible,
                columnsConfig: this.approvalAttachmentsColumnsConfig,
                onRemoveBlock: (event) => {
                  const { key } = event?.block || {}
                  const { updateData, formData } = event || {}

                  if (key && typeof updateData === 'function') {
                    const { files } = formData || {}
                    // console.log(formData, key, files)
                    updateData({
                      files: files?.filter(file => file.key !== key)
                    })
                  }
                },
                buttons: [
                  {
                    label: this.$t('road-maintenance/aggregator.upload_attachment'),
                    color: '#65AD45',
                    prependIcon: 'mdi-plus',
                    prependIconStyle: { color: '#FFFFFF !important' },
                    style: { color: '#FFFFFF !important' },
                    visible: !!this.permissions?.canCreate,
                    click: (event) => this.onApprovalUploadAttachment(event)
                  }
                ],
                dataKey: 'documents'
              }
            ]
          },
          // Document history
          {
            component: this.rowBlocksDataComponent,
            mainClasses: 'aggregator-requests-blocks-main',
            wrapperClasses: 'aggregator-requests-subject-wrapper',
            title: this.$t('road-maintenance/aggregator.log_history'),
            visible: this.approvalHistoryVisible,
            externalData: this.logsData,
            dataKey: DOCUMENTS_HISTORY_DATA_KEY,
            columnsConfig: [
              {
                key: 'created_at',
                title: this.$t('road-maintenance.date') + ':',
                valueClass: 'history-block-value',
                customValue: (data) => {
                  return data?.created_at ? dateService.formatCustom(data.created_at, FORMATS.isoDatetime, FORMATS.datetimeShort) : ''
                }
              },
              {
                key: 'status',
                title: this.$t('road-maintenance/excavation-service.status') + ':',
                valueClass: 'history-block-value',
                customValue: (data) => {
                  return data?.status ? this.$t(data.status) : '-'
                }
              },
              {
                key: 'user',
                valueClass: 'history-block-value',
                title: this.$t('road-maintenance.user') + ':'
              },
              {
                key: 'document',
                title: this.$t('road-maintenance/aggregator.document') + ':',
                visible: (event) => {
                  return event?.blockValue !== '-'
                },
                loaderIds: this.approvalDocumentLoaders,
                valueClass: 'history-block-value',
                prependImage: {
                  src: '/img/icons/pdf-icon.svg',
                  style: {
                    marginRight: '4%'
                  }
                },
                contentWrapperStyle: {
                  display: 'flex',
                  alignItems: 'center'
                },
                customValue: (data) => {
                  return data?.document?.name || '-'
                },
                click: (event) => this.onApprovalDownloadDocument(event)
              },
              {
                visible: (event) => {
                  return !!event?.rowData?.document_data_id
                },
                component: this.createFromVersionBtnComponent,
                class: 'create-from-version-btn-block',
                onClick: (event) => this.onApprovalCreateFromVersion(event)
              }
            ]
          },
          {
            component: this.documentPreviewComponent,
            visible: this.approvalShowDocumentPreview,
            content: this.approvalDocument
          }
        ]
      }
    }
  },

  methods: {
    async onApprovalDownloadDocument (event) {
      const { id: documentId } = event?.rowData || {}

      // skip if download is already in progress
      if (this.approvalDocumentLoaders.includes(documentId)) {
        return
      }
      // show document loader
      if (documentId && !this.approvalDocumentLoaders.includes(documentId)) {
        this.approvalDocumentLoaders.push(documentId)
      }

      // download document
      await this.downloadDocument(event)
      // stop document loader
      if (this.approvalDocumentLoaders.includes(documentId)) {
        this.approvalDocumentLoaders = this.approvalDocumentLoaders.filter(activeLoaderId => activeLoaderId !== documentId)
      }
    },

    async onApprovalGenerateDocument (event, showLast = false, fromWarningPopup = false) {
      try {
        const { formData } = event || {}
        const { id } = this?.$route?.params || {}

        // validate data
        if (!id) {
          notify(this.$t('road-maintenance/aggregator.generate_document_not_possible'))
          console.error('Missing traffic regulation id')
          return
        }
        if (!isObject(formData)) {
          notify(this.$t('road-maintenance/aggregator.generate_document_not_possible'))
          console.error('Form data is invalid', formData)
          return
        }

        // if user previously confirmed that wants to generate document without created solution
        // close warning popup before document generation
        if (fromWarningPopup) {
          try {
            this.$set(this.informationDialogConfig, 'showDialog', false)
            this.$set(this.informationDialogConfig, 'title', this.informationDialogBaseConfig.title)
            this.$set(this.informationDialogConfig, 'confirmCallbackFunction', this.informationDialogBaseConfig.confirmCallbackFunction)
          }
          catch (exception) {
            console.error(exception)
            notify(this.$t('base.application_error'))
          }
        }

        // show tab loader before generating document
        this.showApprovalTabLoader = true

        const dataToSend = new FormData()
        dataToSend.append('valid_from', formData?.valid_from ? dateService.formatCustom(formData.valid_from, FORMATS.datetimeShort, FORMATS.isoDatetime) : '')
        dataToSend.append('valid_to', formData?.valid_to ? dateService.formatCustom(formData.valid_to, FORMATS.datetimeShort, FORMATS.isoDatetime) : '')
        dataToSend.append('created_at', formData?.created_at ? dateService.formatCustom(formData.created_at, FORMATS.date, FORMATS.isoDate) : '')
        dataToSend.append('restoration_responsibility', formData?.restoration_responsibility?.name || formData?.restoration_responsibility || '')
        dataToSend.append('road_authority_responsible_person', formData?.road_authority_responsible_person ?? '')
        dataToSend.append('damage_responsibility', formData?.damage_responsibility?.name || formData?.damage_responsibility || '')
        dataToSend.append('additional_costs_responsibility', formData?.additional_costs_responsibility?.name || formData?.additional_costs_responsibility || '')
        dataToSend.append('additional_terms', formData?.additional_terms?.id ?? formData?.additional_terms ?? '')
        dataToSend.append('payment_type', formData?.payment_type?.name ?? formData?.payment_type ?? '')
        dataToSend.append('additional_terms_note', formData?.additional_terms_note ?? '')
        dataToSend.append('erp_reference_number', formData?.erp_reference_number ?? '')
        dataToSend.append('erp_document_status', formData?.erp_document_status ?? '')
        dataToSend.append('show_last_version', showLast)

        // Get document html
        const { data } = await api('road-maintenance').post(`traffic-regulation/${id}/${DOCUMENT_TYPE}`, dataToSend)

        // get generated document HTML
        this.approvalDocument = data
        // show document preview
        this.approvalShowDocumentPreview = true
        // set tab edit state to 'true' making save and cancel button to be visible
        this.approvalEditState = true
        // show edit buttons
        this.approvalShowEditButtons = true
        // hide form, display document preview
        this.approvalDataForGenerationVisible = false
        this.approvalTitle = this.$t('road-maintenance/aggregator.approval_document_overview')
      }
      catch (exception) {
        const { errors } = exception?.response?.data || {}
        this.approvalValidationErrors = errors && isObject(errors) ? errors : {}
        console.error(exception)
      }
      finally {
        this.showApprovalTabLoader = false
      }
    },

    async onApprovalCreateFromVersion (event) {
      try {
        this.showApprovalTabLoader = true

        // get document data
        const data = await this.createFromVersion(event, DOCUMENT_TYPE)

        const { html, css } = data || {}
        // do not open document if data is invalid
        if (!html && !css) {
          return
        }

        // set document data and open document preview
        this.approvalDocument = data
        this.approvalShowDocumentPreview = true
        this.approvalEditState = true
        this.approvalShowEditButtons = true
        this.approvalDataForGenerationVisible = false
        this.approvalTitle = this.$t('road-maintenance/aggregator.approval_document_overview')
      }
      catch (exception) {
        console.error(exception)
      }
      finally {
        this.showApprovalTabLoader = false
      }
    },

    onApprovalCancelTab () {
      try {
        this.$set(this.informationDialogConfig, 'title', this.$t('base.warning'))
        this.$set(this.informationDialogConfig, 'positiveButtonText', this.$t('base.confirm'))
        this.$set(this.informationDialogConfig, 'negativeButtonText', this.$t('base.cancel'))
        this.$set(this.informationDialogConfig, 'message', this.$t('fleet/vehicle.data_not_saved_message'))
        this.$set(this.informationDialogConfig, 'confirmCallbackFunction', () => this.approvalCancel())
        this.$set(this.informationDialogConfig, 'showDialog', true)
      }
      catch (exception) {
        console.error(exception)
        notify(this.$t('base.application_error'))
      }
    },

    approvalCancel () {
      try {
        this.approvalEditState = false
        this.$set(this.informationDialogConfig, 'showDialog', false)
      }
      catch (exception) {
        console.error(exception)
        notify(this.$t('base.application_error'))
      }
    },

    onApprovalSave (event) {
      try {
        const { saveTab } = event || {}
        this.$set(this.informationDialogConfig, 'title', this.$t('base.warning'))
        this.$set(this.informationDialogConfig, 'positiveButtonText', this.$t('base.confirm'))
        this.$set(this.informationDialogConfig, 'negativeButtonText', this.$t('base.cancel'))
        this.$set(this.informationDialogConfig, 'message', this.$t('road-maintenance/aggregator.document_will_be_uploaded_to_centrix_message'))
        this.$set(this.informationDialogConfig, 'confirmCallbackFunction', () => this.onApprovalSaveConfirmation(saveTab))
        this.$set(this.informationDialogConfig, 'showDialog', true)
      }
      catch (exception) {
        console.error(exception)
        notify(this.$t('base.application_error'))
      }
    },

    onApprovalSaveConfirmation (saveTabFn) {
      try {
        if (typeof saveTabFn === 'function') {
          saveTabFn()
        }

        this.approvalEditState = false
        this.$set(this.informationDialogConfig, 'showDialog', false)
        this.$set(this.informationDialogConfig, 'title', this.informationDialogBaseConfig.title)
        this.$set(this.informationDialogConfig, 'confirmCallbackFunction', this.informationDialogBaseConfig.confirmCallbackFunction)
      }
      catch (exception) {
        console.error(exception)
        notify(this.$t('base.application_error'))
      }
    },

    onApprovalEditUpdate (event) {
      const { edit } = event || {}
      this.approvalHistoryVisible = !edit

      if (!edit) {
        this.approvalShowDocumentPreview = false
        this.approvalEditState = false
        this.approvalShowEditButtons = false
        this.approvalDataForGenerationVisible = true
        this.approvalDocument = null
        this.approvalTitle = this.approvalBaseTitle
      }
    },

    async onApprovalDataFetched (event, afterSave = false) {
      const { data: apiData, updateData } = event || {}
      const { formDataForSend } = event || {}
      const data = apiData?.data || formDataForSend || {}

      if (typeof updateData === 'function' && data && isObject(data)) {
        updateData({
          // Update contractor
          contractor_name: data?.contractor?.name ?? null,
          contractor_responsible: data?.contractor?.responsible_person ?? null,
          contractor_phone: data?.contractor?.phone ?? null,
          contractor_email: data?.contractor?.email ?? null,
          created_at: data?.created_at && !dateService.isValid(data.created_at, FORMATS.date, true) ? dateService.formatCustom(data.created_at, FORMATS.isoDatetime, FORMATS.date) : dateService.now(FORMATS.date),

          // Regulation setter
          regulation_setter: data?.regulation_setter?.description ? this.$t(data.regulation_setter.description) : null,

          // datetime from and to
          date_time_from: data?.date_time_from ? dateService.formatCustom(data.date_time_from, FORMATS.isoDatetime, FORMATS.datetimeShort) : null,
          date_time_to: data?.date_time_to ? dateService.formatCustom(data.date_time_to, FORMATS.isoDatetime, FORMATS.datetimeShort) : null,
          valid_from: data?.valid_from ? dateService.formatCustom(data.valid_from, FORMATS.isoDatetime, FORMATS.datetimeShort) : null,
          valid_to: data?.valid_to ? dateService.formatCustom(data.valid_to, FORMATS.isoDatetime, FORMATS.datetimeShort) : null
        })
      }

      if (!this.approvalDocumentHistoryLoaded || afterSave) {
        this.approvalDocumentHistoryLoaded = true
        const { id: suggestedStatusId, description } = data?.suggested_status || {}

        // fetch documents logs
        await this.fetchHistoryDocuments(DOCUMENT_TYPE, DOCUMENTS_HISTORY_DATA_KEY)

        // open change status popup after documents save only if suggested status provided
        if (suggestedStatusId) {
          try {
            this.$set(this.afterDocumentStatusChangeActions, 'statusId', suggestedStatusId)
            const message = this.$t('road-maintenance/aggregator.status_change_message')
            this.$set(this.informationDialogConfig, 'showDialog', afterSave)
            this.$set(this.informationDialogConfig, 'message', '<div> ' + message + '<span style="font-style: italic; color: green;">' + ' ' + this.$t(description) + '</span>?</div>')
          }
          catch (exception) {
            console.error(exception)
            notify(this.$t('base.application_error'))
          }
        }
      }
    },

    onApprovalModifyData (event) {
      const { originalData } = event || {}

      if (originalData) {
        originalData.created_at = originalData?.created_at ? dateService.formatCustom(originalData.created_at, FORMATS.date, FORMATS.isoDate) : null
        originalData.valid_from = originalData?.valid_from ? dateService.formatCustom(originalData.valid_from, FORMATS.datetimeShort, FORMATS.isoDatetime) : null
        originalData.valid_to = originalData?.valid_to ? dateService.formatCustom(originalData.valid_to, FORMATS.datetimeShort, FORMATS.isoDatetime) : null
        if (originalData?.files?.length) {
          originalData.documents = originalData.files?.map(file => file?.file || null)
          delete originalData.files
        }
        else {
          delete originalData.documents
          delete originalData.files
        }
      }

      return originalData
    },

    onApprovalUploadAttachment (event) {
      if (!this.$refs?.file_input) {
        notify(this.$t('base.application_error'))
        return
      }

      this.$refs.file_input.value = ''
      this.$refs.file_input.click()
      this.approvalUploadAttachmentEvent = event
    },

    onDocumentInput (event) {
      try {
        const uploadedFiles = Array.from(event?.target?.files || [])
        const { updateData, formData } = this.approvalUploadAttachmentEvent || {}
        const existingObject = formData?.documents?.[0] || {}

        const allFiles = [
          ...Object.values(existingObject),
          ...uploadedFiles
        ]

        if (!allFiles.length || typeof updateData !== 'function') {
          notify(this.$t('base.application_error'))
          return
        }

        const newObject = {}
        this.approvalAttachmentsColumnsConfig = allFiles.map((file, index) => {
          const key = `document_${index}`
          newObject[key] = file

          const returnObject = {
            key,
            deletable: true,
            title: this.$t('road-maintenance/aggregator.document') + ':',
            customValue: () => {
              return file?.name || file?.file_name || '-'
            }
          }

          // download is possible only if attachment is on Centrix
          if (file?.file_id) {
            returnObject.click = () => this.downloadApprovalAttachment(file)
          }
          return returnObject
        })

        const files = Array.isArray(allFiles)
          ? allFiles
            .filter(file => file && file.name)
            .map((file, index) => ({
              file,
              key: `document_${index}`
            }))
          : []

        updateData({
          documents: [newObject]
        })

        if (files?.length) {
          updateData({
            files
          })
        }
      }
      catch (exception) {
        notify(this.$t('base.application_error'))
        console.error(exception)
      }
    },

    async approvalMenuItemClick (event) {
      const { menuIndex, formData } = event || {}
      const { ruling_exists: rulingExists } = formData || {}

      // if solution is not created, show warning popup before approval generation
      if (!rulingExists) {
        try {
          this.$set(this.informationDialogConfig, 'title', this.$t('base.warning'))
          this.$set(this.informationDialogConfig, 'positiveButtonText', this.$t('base.yes'))
          this.$set(this.informationDialogConfig, 'negativeButtonText', this.$t('base.no'))
          this.$set(this.informationDialogConfig, 'message', this.$t('road-maintenance/aggregator.solution_not_created_message'))
          this.$set(this.informationDialogConfig, 'confirmCallbackFunction', () => this.onApprovalGenerateDocument(event, menuIndex === 1, true))
          this.$set(this.informationDialogConfig, 'showDialog', true)
        }
        catch (exception) {
          notify(this.$t('base.application_error'))
          console.error(exception)
        }
      }
      else {
        await this.onApprovalGenerateDocument(event, menuIndex === 1)
      }
    },

    async downloadApprovalAttachment (attachment) {
      let objectURL = null
      try {
        const { file_id: fileId } = attachment || {}
        if (fileId) {
          const url = new URL(`${window.location.origin}/api/road-maintenance/traffic-regulation/${this.$route.params.id}/attachment/${fileId}`)

          const response = await fetch(url.toString(), {
            method: 'POST'
          })

          if (!response.ok) {
            console.error(`Failed to show PDF: HTTP ${response.status} - ${response.statusText}`)
            notify(`Failed to show PDF: HTTP ${response.status} - ${response.statusText}`)
            return
          }

          const contentType = response.headers.get('Content-type')
          const contentDisposition = response.headers.get('Content-Disposition')
          const arrayBuffer = await response.arrayBuffer()

          // Create a Blob from the response data
          const blob = new Blob([new Uint8Array(arrayBuffer)], {
            type: contentType
          })

          // Generate object URL for downloading
          objectURL = URL.createObjectURL(blob)
          const filename = contentDisposition?.split('filename=')[1] ?? 'File'

          // Trigger download
          const link = document.createElement('a')
          link.href = objectURL
          link.download = filename
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        }
      }
      catch (exception) {
        console.error(exception.message || exception)
        notify(exception.message || exception)
      }
      finally {
        if (objectURL) {
          URL.revokeObjectURL(objectURL)
        }
      }
    }
  }
}
